import React from 'react';
import { QueryClient, QueryClientProvider } from 'react-query';
import { MINUTE_IN_MS } from '@fiverr-private/futile';
import { useConst } from '@fiverr-private/hooks';
import {
    AtomsProvider,
    LocalizationContextProvider,
    PageName,
    localizationDataAtom,
    pageNameAtom,
    floatingChatDataAtom,
} from '@fiverr-private/seller_pages_toolbox';
import { ComplianceProvider } from '@fiverr-private/compliance';
import { getContext } from '@fiverr-private/fiverr_context';
import { agencyAtom, gigsAtom, reviewsAtom, customOrderModalAtom, rolloutsAtom, agencySellerAtom } from '../../atoms';
import { AgencyPage } from '../../components/AgencyPage';
import { AppProps } from './types';

const App = ({
    agency,
    currency,
    gigs,
    localization,
    reviews,
    seller,
    rollouts,
    floatingChatData,
    complianceData,
    isPriceUpfrontEnabled,
    priceUpfrontConfiguration,
    priceUpfrontContext,
}: AppProps) => {
    const queryClient = useConst(
        () =>
            new QueryClient({
                defaultOptions: {
                    queries: {
                        staleTime: 5 * MINUTE_IN_MS,
                        cacheTime: typeof window === 'undefined' ? undefined : 10 * MINUTE_IN_MS,
                        refetchOnWindowFocus: false,
                    },
                },
            })
    );
    const { userId, organizationId } = getContext();
    const pageName = 'agency_page' as PageName;

    return (
        <AtomsProvider
            atoms={[
                [agencyAtom, agency],
                [gigsAtom, gigs],
                [localizationDataAtom, localization],
                [reviewsAtom, reviews],
                [agencySellerAtom, seller],
                [pageNameAtom, pageName],
                [customOrderModalAtom, { isOpen: false, data: null }],
                [rolloutsAtom, rollouts],
                [floatingChatDataAtom, floatingChatData],
            ]}
        >
            <QueryClientProvider client={queryClient}>
                <ComplianceProvider
                    complianceData={complianceData?.value?.state}
                    requester_id={userId}
                    organizationId={organizationId}
                    pageName={pageName}
                    isPriceUpfrontEnabled={isPriceUpfrontEnabled}
                    priceUpfrontConfiguration={priceUpfrontConfiguration}
                    priceUpfrontContext={priceUpfrontContext}
                >
                    <LocalizationContextProvider currency={currency}>
                        <AgencyPage />
                    </LocalizationContextProvider>
                </ComplianceProvider>
            </QueryClientProvider>
        </AtomsProvider>
    );
};

export default App;
